import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Stack,
  Button,
  useTheme,
  CircularProgress,
} from "@mui/material";
import HugeIcon from "@/components/HugeIcon/DynamicHugeIcon";
import { useCopilot } from "../CopilotProvider";
import { ChatBotIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";

const SUGGESTIONS = [
  "Activar o desactivar servicio de delivery",
  "Actualizar precio de un plato",
  "Crear un nuevo plato",
];

const CopilotDrawer: React.FC = () => {
  const { isOpen, toggleDrawer, sendMessage, messages, isLoading } = useCopilot();
  const [inputValue, setInputValue] = useState("");
  const { palette } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSend = () => {
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!isLoading) {
      sendMessage(suggestion);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={toggleDrawer}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 500 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid #eee",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HugeIcon name="ai-magic" size={24} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
            Copilot
          </Typography>
          <Chip
            label="BETA"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: "#E8F0FE",
              color: "#1967D2",
            }}
          />
        </Box>
        <Box>
          <IconButton size="small" onClick={() => console.log("reset chat")}>
            <HugeIcon name="refresh" size={20} />
          </IconButton>
          <IconButton size="small" onClick={toggleDrawer}>
            <HugeIcon name="cancel-01" size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {messages.length === 0 ? (
          <Stack flex={1} spacing={1} justifyContent="center" alignItems="center" p={3}>
            <HugeiconsIcon icon={ChatBotIcon} color={palette.primary.main} size={56} strokeWidth={1} />
            <Typography variant="h4" textAlign="center">
              ¿Cómo puedo ayudarte?
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Pídeme que haga algo por ti o elige una sugerencia para empezar.
            </Typography>
            {SUGGESTIONS.map((suggestion, index) => (
              <Button key={index} variant="outlined" fullWidth onClick={() => handleSuggestionClick(suggestion)}>
                {suggestion}
              </Button>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2} p={3}>
            {messages.map((msg) => {
              const isUser = msg.role === "user";
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "80%",
                      p: 2,
                      borderRadius: 3,
                      borderTopRightRadius: isUser ? 0 : 3,
                      borderTopLeftRadius: !isUser ? 0 : 3,
                      bgcolor: isUser ? "primary.main" : "grey.100",
                      color: isUser ? "primary.contrastText" : "text.primary",
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {msg.content}
                    </Typography>
                  </Box>
                  {msg.functionCall && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <HugeIcon name="settings-01" size={14} color={palette.text.secondary} />
                      <Typography variant="caption" color="text.secondary">
                        Ejecutó: {msg.functionCall.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
            {isLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Escribiendo...
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Stack>
        )}
      </Box>

      <Divider />

      {/* Input Area */}
      <Box sx={{ p: 2, pb: 3 }}>
        <TextField
          fullWidth
          placeholder="Escribe un mensaje..."
          variant="outlined"
          size="medium"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSend();
          }}
          disabled={isLoading}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              pr: 1,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  sx={{
                    backgroundColor: inputValue.trim() ? "#e0e7ff" : "transparent",
                    borderRadius: 2,
                  }}
                >
                  {isLoading ? <CircularProgress size={20} /> : <HugeIcon name="sent" size={20} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Drawer>
  );
};

export default CopilotDrawer;
